import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  //Divider,
  AppBar,
  Toolbar,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  OutlinedInput,
  InputLabel,
  Tab,
  Tabs,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, TableOfContents } from 'docx';
import { pdf } from '@react-pdf/renderer';
import { getPredefinedSAPSections } from '../constants/sapSectionData';
import ScrollToTop from '../../components/common/ScrollToTop';
import { generateContent } from '../../services/AIService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import documentEditorService from '../../services/documentEditor.service';
import { useProject } from '../../context/ProjectContext';
import sapIntakeService from '../services/sapIntake.service';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import { fetchLatestClinicalIntakeData } from '../../services/clinicalIntake.service';
import { colors as dizzarooColors } from '../../theme/tokens';
import SAPPdfDocument, { buildSapPdfTocEntries } from '../../components/documentEditor/SAPPdfDocument';
import { richTextToBlocks } from '../../utils/richTextToBlocks';
import { startSpan, endSpan } from '../../telemetry/telemetry';
//import clinicalIntakeService from '../../services/clinicalIntake.service';

// Helper functions for XML export
function toCamelCase(str) {
  if (!str) return 'section';
  // Remove any characters that are invalid in XML tag names
  return str
    .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/(?:^|\s)([a-z])/g, (_, char) => char.toUpperCase()) // Capitalize first letter after space
    .replace(/\s+/g, '') // Remove all spaces
    .replace(/^(.)/, char => char.toLowerCase()) // Ensure first character is lowercase
    .replace(/^[^a-z]/i, 'section'); // If starts with non-letter, use 'section'
}

function parseSectionContent(content, mainNum) {
  const subsections = [];
  const subsectionRegex = new RegExp(`^${mainNum}\\.\\d+\\s+(.+)\\n([\\s\\S]*?)(?=\\n${mainNum}\\.\\d+\\s+|$)
`, 'gm');
  let match;
  let lastIndex = 0;
  let mainContent = '';

  while ((match = subsectionRegex.exec(content)) !== null) {
    // Add the content before this subsection to mainContent
    mainContent += content.substring(lastIndex, match.index);

    subsections.push({
      number: match[0].split(' ')[0],
      title: (match[1] || '').trim(),
      content: (match[2] || '').trim(),
    });
    lastIndex = subsectionRegex.lastIndex;
  }

  // Add any remaining content after the last subsection to mainContent
  mainContent += content.substring(lastIndex);

  return { main: (mainContent || '').trim(), subsections };
}

// Helper function to escape XML special characters
function escapeXml(text) {
  if (typeof text !== 'string') return text;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function jsonToXml(obj, tagName, indent = '') {
  let xml = `${indent}<${tagName}>\n`;
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (Array.isArray(value)) {
        for (const item of value) {
          xml += jsonToXml(item, key.slice(0, -1), indent + '  '); // Use singular tag name for array items
        }
      } else if (typeof value === 'object' && value !== null) {
        xml += jsonToXml(value, key, indent + '  ');
      } else {
        xml += `${indent}  <${key}>${escapeXml(value)}</${key}>\n`;
      }
    }
  }
  xml += `${indent}</${tagName}>`;
  return xml;
}

// Dizzaroo brand theme – Primary: Deep Blue #1E73BE (fallbacks so CircularProgress never sees undefined palette.primary)
const theme = createTheme({
  palette: {
    primary: {
      main: dizzarooColors?.primary?.[500] ?? '#1E73BE',
      light: dizzarooColors?.primary?.light ?? '#4A8FD4',
      dark: dizzarooColors?.primary?.[600] ?? '#1E6091',
      contrastText: '#ffffff',
    },
    secondary: {
      main: dizzarooColors?.accent?.teal ?? '#168AAD',
      light: dizzarooColors?.accent?.tealLight ?? '#168AAD',
      dark: dizzarooColors?.accent?.tealDark ?? '#168AAD',
      contrastText: '#ffffff',
    },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Urbanist", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Urbanist", sans-serif', fontWeight: 600 },
    subtitle1: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    subtitle2: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
    body1: { fontFamily: '"Urbanist", sans-serif' },
    body2: { fontFamily: '"Urbanist", sans-serif' },
    button: { fontFamily: '"Urbanist", sans-serif', fontWeight: 500 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800&display=swap');
      `,
    },
    MuiButton: {
      defaultProps: { color: 'primary', disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8 },
        contained: {
          '&.MuiButton-colorPrimary': {
            backgroundColor: dizzarooColors?.primary?.[500] ?? '#1E73BE',
            '&:hover': { backgroundColor: dizzarooColors?.primary?.[600] ?? '#1E6091' },
          },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 4px rgba(0,0,0,0.05)' } } },
  },
});

const generateSapTableOfContents = () => {
  // Use a Word-native TOC field so Word can calculate page numbers dynamically.
  // Headings included will be all paragraphs with HeadingLevel 1–6 that we already
  // generate per section/subsection; the index text below is just a visual guide.
  const tocParagraphs = [];

  // TOC title
  tocParagraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Table of Contents',
          size: 28, // 14pt
          bold: false,
          color: '000000',
        }),
      ],
      heading: HeadingLevel.HEADING_1,
    })
  );

  // Native TOC field: Word will populate headings + page numbers on update
  tocParagraphs.push(
    new TableOfContents('', {
      hyperlink: true,
      headingStyleRange: '1-6',
      useHeadingStyles: true,
      includePageNumbers: true,
      pageNumbersRightAligned: true,
      showPageNumbers: true,
      tabLeader: 'dot',
    })
  );

  return tocParagraphs;
};

// Strips a leading numbering prefix (e.g. "3.", "3.0", "13.2") so heading
// text can be compared ignoring numbering-format differences.
function stripLeadingNumber(text) {
  return String(text || '').replace(/^\s*\d+(\.\d+)*\.?\s*/, '').trim();
}

function normalizeHeadingText(text) {
  return stripLeadingNumber(text).toLowerCase().replace(/\s+/g, ' ').trim();
}

// True when a generated section's first heading just restates the section
// title (e.g. content beginning "3.0 STATISTICAL FRAMEWORK" under a section
// already titled "3. Statistical Framework" in the export). Used to drop
// that redundant heading so the title isn't printed twice in exports.
function isDuplicateTitleHeading(headingText, sectionTitle) {
  const a = normalizeHeadingText(headingText);
  const b = normalizeHeadingText(sectionTitle);
  return Boolean(a) && a === b;
}

function blocksToDocxChildren(blocks) {
  const children = [];
  if (!Array.isArray(blocks)) return children;

  blocks.forEach((b) => {
    if (b.type === 'heading') {
      const lvl = b.level || 1;
      const headingLevel =
        lvl === 1 ? HeadingLevel.HEADING_1 :
        lvl === 2 ? HeadingLevel.HEADING_2 :
        lvl === 3 ? HeadingLevel.HEADING_3 :
        HeadingLevel.HEADING_4;
      children.push(
        new Paragraph({
          heading: headingLevel,
          children: [
            new TextRun({
              text: b.text || '',
              size: 28, // 14pt for headings
              bold: true,
              color: '000000',
            }),
          ],
        })
      );
    } else if (b.type === 'paragraph') {
      const runs = (b.runs || [{ text: '' }]).map(
        (r) =>
          new TextRun({
            text: r.text || '',
            bold: !!r.bold,
            italics: !!r.italic,
            size: 24, // 12pt for body text
            color: '000000',
          })
      );
      children.push(new Paragraph({ children: runs }));
    } else if (b.type === 'list') {
      (b.items || []).forEach((runs) => {
        const text = (runs || []).map((r) => r.text || '').join('');
        children.push(
          new Paragraph({
            text,
            bullet: { level: 0 },
            style: undefined,
          })
        );
      });
    } else if (b.type === 'table') {
      // Skip rows with zero cells: a <w:tr> with no <w:tc> children is
      // invalid OOXML and makes Word report the whole document as
      // unreadable/corrupt. This happens for stray/malformed <tr> markup
      // (e.g. an empty separator row) that the source HTML can contain.
      const rows = (b.rows || [])
        .filter((row) => Array.isArray(row) && row.length > 0)
        .map(
          (row) =>
            new TableRow({
              children: row.map(
                (cellRuns) =>
                  new TableCell({
                    // rowSpan/colSpan (from richTextToBlocks' table parsing) preserve a
                    // merged first-column cell across spanned rows — docx's own Table
                    // builder auto-inserts the vertical-merge continuation cells into the
                    // following rows from these, so omitting them here is what shifts each
                    // spanned row's remaining cells one column left in the exported docx.
                    ...(cellRuns?.rowSpan > 1 ? { rowSpan: cellRuns.rowSpan } : {}),
                    ...(cellRuns?.colSpan > 1 ? { columnSpan: cellRuns.colSpan } : {}),
                    children: [
                      new Paragraph({
                        children: (cellRuns || []).map(
                          (r) =>
                            new TextRun({
                              text: r.text || '',
                              bold: !!r.bold,
                              italics: !!r.italic,
                              size: 24, // 12pt in tables
                              color: '000000',
                            })
                        ),
                      }),
                    ],
                  })
              ),
            })
        );
      if (rows.length > 0) {
        children.push(
          new Table({
            rows,
          })
        );
      }
    }
  });

  return children;
}

// Mapping from SAP section titles to intake fields
const intakeToSectionMap = {
  '1. Administrative Information': [
    'protocol_id', 'protocol_version', 'sponsor_name', 'statistician_contact'
  ],
  '2. INTRODUCTION AND STUDY OVERVIEW': [
    'study_phase', 'indication', 'primary_objective', 'secondary_objectives', 'design_type'
  ],
  '3. STUDY OBJECTIVES, ESTIMANDS, AND ENDPOINTS': [
    'itt_definition', 'pp_definition', 'safety_pop_definition', 'other_sets', 'blinding_level', 'strat_factors', 'randomisation_ratio', 'analysis_software', 'adam_version', 'ae_coding_dict', 'lab_grade_scale'
  ],
  '4. Efficacy Analyses': [
    'primary_endpoint', 'effect_size', 'assumed_control_mean', 'alpha_two_sided', 'power', 'dropout_rate', 'calc_method', 'estimand_primary', 'estimand_secondary', 'subgroup_list', 'hierarchy_order'
  ],
  '5. Safety Analyses': [
    'teae_window', 'pk_parameters', 'pd_endpoints', 'pro_instruments', 'safety_pop_definition'
  ],
  '6. Other Analyses (if applicable)': [
    'mult_strategy', 'general_missing', 'imputation_method', 'tipping_point_flag', 'scoring_algorithm_ref'
  ],
  '7. Interim Analyses & Data Monitoring (optional)': [
    'interim_planned', 'interim_timing', 'alpha_spend_func', 'dmc_involved'
  ],
  '8. Handling of Missing or Incomplete Data': [
    'general_missing', 'imputation_method', 'tipping_point_flag'
  ],
  '9. EFFICACY ANALYSES AND SUMMARIES': [
    'primary_endpoint', 'secondary_endpoints', 'exploratory_endpoints', 'analysis_software'
  ],
  '10. SAFETY ANALYSES AND SUMMARIES': [
    'ae_coding_dict', 'lab_grade_scale', 'teae_window', 'safety_pop_definition'
  ],
  '11. PHARMACOKINETIC (PK) ANALYSES': [
    'pk_parameters', 'pk_sampling_schedule', 'pk_analysis_set'
  ],
  '12. INTERIM ANALYSES': [
    'interim_planned', 'interim_timing', 'alpha_spend_func', 'dmc_involved'
  ],
  '13. CHANGES IN THE PLANNED ANALYSIS': [
    'prior_sap_version', 'change_reason'
  ],
  '12. Appendices': [
    'fileInfo'
  ]
};

function buildSectionContent(sectionTitle, intakeData) {
  const fields = intakeToSectionMap[sectionTitle] || [];
  return fields
    .map(field => {
      const value = intakeData[field];
      if (value === undefined || value === null || value === '') return '';
      if (typeof value === 'object') return `${field}: ${JSON.stringify(value, null, 2)}`;
      return `${field}: ${value}`;
    })
    .filter(Boolean)
    .join('\n');
}

// Helper to get the default prompt for a section title
let defaultPromptMap = null;
async function getDefaultPromptForSection(title, projectId) {
  if (!defaultPromptMap) {
    const sections = await getPredefinedSAPSections(projectId);
    defaultPromptMap = {};
    for (const s of sections) {
      defaultPromptMap[s.title] = s.prompt;
    }
  }
  return defaultPromptMap[title] || '';
}

const SAPEditor = () => {
  const navigate = useNavigate();
  const { id: documentId } = useParams(); // Using documentId from URL params for specific document fetches
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();

  // Determine the actual projectId to use
  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [sections, setSections] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isGenerating, setIsGenerating] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableSectionTitles, setAvailableSectionTitles] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [mode, setMode] = useState('preview'); // 'edit' or 'preview'
  const [editedTextContent, setEditedTextContent] = useState({}); // Track edited text content

  // KPI: user-perceived editor load (route open → sections ready)
  useEffect(() => {
    startSpan('ui_sap_editor_load', { page: 'sap_editor', meta: { projectId: actualProjectId || projectIdFromUrl || null } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && !projectContextLoading && actualProjectId) {
      endSpan('ui_sap_editor_load', { status: error ? 'error' : 'ok' });
    }
  }, [isLoading, projectContextLoading, actualProjectId, error]);

  // State for Add Section dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedSectionTitle, setSelectedSectionTitle] = useState('');
  const [customSectionTitle, setCustomSectionTitle] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 for Template, 1 for Custom

  // Add to SAPEditor state:
  const [sectionTabs, setSectionTabs] = useState({}); // { [sectionId]: 0 for Edit, 1 for Version History }
  const [sectionVersions, setSectionVersions] = useState({}); // { [sectionId]: [versions] }
  const [trialSchemaImageUrl, setTrialSchemaImageUrl] = useState(null); // Trial schema image for Section 2 preview

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      console.log('SAPEditor: Loading project from URL:', projectIdFromUrl);
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  // Keyboard shortcut to toggle preview mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        setMode(mode === 'edit' ? 'preview' : 'edit');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode]);

  // Fetch available section titles and sections on component mount, or when project changes
  useEffect(() => {
    if (actualProjectId && !projectContextLoading) {
      fetchSectionTitles();
      fetchSections();
      fetchTrialSchemaImage();
    } else if (!actualProjectId && !projectContextLoading) {
      setError('Please select a project to view its documents.');
      setIsLoading(false);
    }
  }, [actualProjectId, projectContextLoading]); // Re-run when actualProjectId or projectContextLoading changes

  // Fetch trial schema image (same source as protocol/clinical intake)
  const fetchTrialSchemaImage = async () => {
    try {
      if (!actualProjectId) return;
      const response = await clinicalIntakeService.fetchLatestClinicalIntakeWithTrialSchemaFromApi(actualProjectId);
      if (!response) {
        setTrialSchemaImageUrl(null);
        return;
      }
      const trialSchemaFiles = response?.document_uploads?.uploaded_files?.trial_schema || [];
      if (trialSchemaFiles.length > 0) {
        const file = trialSchemaFiles[0];
        const imageUrl = file.fileUrl || file.azureBlobUrl || file.url || null;
        setTrialSchemaImageUrl(imageUrl);
      } else if (response.trialSchemaSvg) {
        setTrialSchemaImageUrl(response.trialSchemaSvg);
      } else {
        setTrialSchemaImageUrl(null);
      }
    } catch (err) {
      console.error('Error fetching trial schema image for SAP:', err);
      setTrialSchemaImageUrl(null);
    }
  };

  // Fetch available section titles
  const fetchSectionTitles = async () => {
    try {
      const titles = await getPredefinedSAPSections(actualProjectId); // Pass projectId
      setAvailableSectionTitles(titles);
      setSelectedSectionTitle(titles[0] || ''); // Set default to first available
    } catch (err) {
      console.error('Error fetching SAP section titles:', err);
      setError('Failed to load available SAP section titles.');
    }
  };

  // Fetch sections
  const fetchSections = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedSections = await documentEditorService.getAllSAPSections(actualProjectId); // Pass projectId

      // Only generate sections if there are absolutely no sections for this project
      if (!fetchedSections || fetchedSections.length === 0) {
        // Try to fetch SAP Intake form data
        let intakeData = null;
        try {
          intakeData = await sapIntakeService.fetchLatestSAPIntakeData(actualProjectId);
          console.log('✅ SAP Intake data fetched successfully:', intakeData ? 'Data exists' : 'No data');
          if (intakeData) {
            console.log('📊 SAP Intake fields:', Object.keys(intakeData).length, 'fields');
          }
        } catch (err) {
          console.error('❌ Error fetching SAP Intake data:', err);
        }
        const predefinedSections = await getPredefinedSAPSections(actualProjectId);
        console.log('📋 Predefined sections loaded:', predefinedSections.length, 'sections');
        if (predefinedSections.length > 0) {
          console.log('📄 First section context length:', predefinedSections[0].context?.length || 0, 'chars');
        } // Pass projectId

        for (const section of predefinedSections) {
          try {
            // Double check if section exists (in case it was created while we were processing)
            const existingSections = await documentEditorService.getAllSAPSections(actualProjectId); // Pass projectId
            const existingSection = existingSections.find(s => s.title === section.title && s.projectId === actualProjectId);

            if (existingSection) {
              console.log(`Section "${section.title}" already exists for this project, skipping...`);
              continue;
            }

            // Decide if prompt mentions SAP Intake
            const promptMentionsIntake = /sap intake/i.test(section.prompt);
            let context = section.context || '';
            if (promptMentionsIntake && intakeData) {
              context += '\n\nSAP Intake Data:\n' + buildSectionContent(section.title, intakeData);
            }

            // Section 2: use ONLY SAP intake + 4.1 Description of Trial Design (truncated before 'Decentralized Trial Elements')
            if (section.title === '2. INTRODUCTION AND STUDY OVERVIEW') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 2: Use ONLY the following. Do not use other protocol sections.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '4.1 Description of Trial Design',
                    ],
                  },
                ];
                let protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  const idx = protocolContext.toLowerCase().indexOf('decentralized trial elements'.toLowerCase());
                  if (idx !== -1) {
                    protocolContext = protocolContext.substring(0, idx);
                  }
                  parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 2 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 2 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 3: use ONLY SAP intake + Protocol Section 3 (Trial Objectives and Associated Estimands)
            if (section.title === '3. STUDY OBJECTIVES, ESTIMANDS, AND ENDPOINTS') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 3: Use ONLY the following. Do not use other protocol sections.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 3 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 3 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 4: use ONLY SAP intake + Protocol Sections 3 and 10 (endpoints & considerations)
            if (section.title === '4. ENDPOINTS AND BASELINE VARIABLES') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 4: Use ONLY the following. Do not use other protocol sections.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                      '10. STATISTICAL CONSIDERATIONS',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 4 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 4 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 5: use ONLY SAP intake + Protocol Sections 4 and 10 (design & statistical considerations)
            if (section.title === '5. HYPOTHESES AND SAMPLE SIZE CONSIDERATIONS') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 5: Use ONLY the following. Do not use other protocol sections.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '4. TRIAL DESIGN',
                      '10. STATISTICAL CONSIDERATIONS',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 5 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 5 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 6: use ONLY Protocol Section 10.2 (Analysis Sets)
            if (section.title === '6. ANALYSIS SETS (POPULATIONS FOR ANALYSIS)') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 6: Use ONLY the following. Do not use other protocol sections.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '10.2 Analysis Sets',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 6 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 6 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 7: use ONLY SAP intake + Protocol Section 10 (Statistical Considerations)
            if (section.title === '7. GENERAL STATISTICAL METHODOLOGY AND CONVENTIONS') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 7: Use ONLY the following. Do not use other protocol sections.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '10. STATISTICAL CONSIDERATIONS',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 7 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 7 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 8: use SAP intake + FULL protocol (all main sections)
            if (section.title === '8. BASELINE, STUDY CONDUCT, AND PARTICIPANT CHARACTERISTICS') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 8: Use ONLY the following. Do not use other protocol sections outside this context.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '1. PROTOCOL SUMMARY',
                      '2. INTRODUCTION',
                      '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                      '4. TRIAL DESIGN',
                      '5. TRIAL POPULATION',
                      '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
                      '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
                      '8. TRIAL ASSESSMENTS AND PROCEDURES',
                      '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
                      '10. STATISTICAL CONSIDERATIONS',
                      '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
                      '12. APPENDIX: SUPPORTING DETAILS',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== FULL PROTOCOL CONTEXT FOR SECTION 8 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 8 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 9: use SAP intake + Protocol Sections 4, 8, and 10 (design, assessments, statistics)
            if (section.title === '9. EFFICACY ANALYSES AND SUMMARIES') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 9: Use ONLY the following. Do not use other protocol sections outside this context.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '4. TRIAL DESIGN',
                      '8. TRIAL ASSESSMENTS AND PROCEDURES',
                      '10. STATISTICAL CONSIDERATIONS',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 9 ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 9 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 10: use SAP intake + Protocol Sections 3, 8, 12, and SOA (Schedule of Activities)
            if (section.title === '10. SAFETY ANALYSES AND SUMMARIES') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 10: Use ONLY the following. Do not use other protocol sections outside this context.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                      '8. TRIAL ASSESSMENTS AND PROCEDURES',
                      '12. APPENDIX: SUPPORTING DETAILS',
                      '1.3 SCHEDULE OF ACTIVITIES',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 10 (SAFETY) ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 10 initial generation:', e);
              }
              context = parts.join('');
            }

            // Section 11: use SAP intake + Protocol Sections 3, 8, and SOA for PK
            if (section.title === '11. PHARMACOKINETIC (PK) ANALYSES') {
              const parts = [];
              parts.push('CONTEXT FOR SECTION 11: Use ONLY the following. Do not use other protocol sections outside this context.');
              parts.push('\n--- SAP INTAKE FORM DATA ---\n');
              parts.push(section.context || '(none provided)');
              try {
                const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
                const protocolSources = [
                  {
                    type: 'PROTOCOL',
                    titles: [
                      '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                      '8. TRIAL ASSESSMENTS AND PROCEDURES',
                      '1.3 SCHEDULE OF ACTIVITIES',
                    ],
                  },
                ];
                const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
                if (protocolContext && protocolContext.trim()) {
                  parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 11 (PK) ===\n');
                  parts.push(protocolContext.trim());
                }
              } catch (e) {
                console.warn('Could not fetch protocol context for Section 11 initial generation:', e);
              }
              context = parts.join('');
            }

            console.log(`🔧 Generating section: ${section.title}`);
            console.log(`📝 Context length for this section: ${context.length} chars`);
            console.log(`🔍 Prompt mentions intake: ${promptMentionsIntake}`);
            
            // Always use the prompt, context may or may not include intake data
            const output = await generateContent(section.prompt || '', context, '', 'sap', section.title);

            await documentEditorService.createSAPSection({
              title: section.title,
              context,
              prompt: '', // Always empty for the user
              output,
              projectId: actualProjectId // Ensure projectId is passed here
            }, actualProjectId); // Pass projectId to service call
          } catch (createErr) {
            console.error("Failed to create or version SAP section:", createErr);
            // Continue processing other sections even if one fails
          }
        }
        // After attempting to create all predefined sections, fetch the actual sections for the project
        const updatedSections = await documentEditorService.getAllSAPSections(actualProjectId); // Pass projectId
        setSections(updatedSections);
      } else {
        setSections(fetchedSections); // Set fetched sections if they exist
      }
    } catch (err) {
      setError('Failed to load SAP sections.');
      console.error('Error fetching SAP sections:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to fetch versions for a section
  const fetchSectionVersions = async (sectionId) => {
    try {
      const versionsData = await documentEditorService.getAllSAPVersions(sectionId, actualProjectId);
      setSectionVersions(prev => ({ ...prev, [sectionId]: versionsData.versions || [] }));
    } catch (err) {
      console.error('Error fetching SAP section versions:', err);
      setSectionVersions(prev => ({ ...prev, [sectionId]: [] }));
    }
  };

  // Helper functions for HTML text editing
  const extractTextContent = (htmlContent) => {
    if (!htmlContent) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const handleInputChange = async (_id, field, value, provenance) => {
    const updatedSections = sections.map((section) =>
      section._id === _id ? { ...section, [field]: value } : section
    );
    setSections(updatedSections);
    try {
      await documentEditorService.updateSAPSection(
        _id,
        { [field]: value, ...(provenance ? { provenance } : {}) },
        actualProjectId
      ); // Pass projectId
    } catch (error) {
      console.error('Error updating SAP section:', error);
    }
  };

  // Handle text content changes while preserving HTML structure
  const handleTextContentChange = async (_id, field, newText) => {
    // Update local state immediately for responsive UI
    setEditedTextContent(prev => ({
      ...prev,
      [_id]: newText
    }));
    
    try {
      // Find the current section to get the original HTML
      const currentSection = sections.find(s => s._id === _id);
      if (!currentSection) return;
      
      // For now, we'll preserve the HTML structure by keeping the original HTML
      // In a more sophisticated implementation, you'd map text changes back to HTML
      // For simplicity, we'll update the output with the text content wrapped in basic HTML
      const updatedHTML = `<p>${newText.replace(/\n/g, '</p><p>')}</p>`;
      
      await handleInputChange(_id, field, updatedHTML);
    } catch (error) {
      console.error('Error updating text content:', error);
    }
  };

  const handleGenerateContent = async (_id) => {
    setIsGenerating((prev) => ({ ...prev, [_id]: true }));
    setError(null);
    try {
      const sectionToGenerate = sections.find((s) => s._id === _id);
      if (!sectionToGenerate) {
        throw new Error('SAP section not found for content generation.');
      }
      // Use user prompt if present, else default prompt
      let promptToUse = sectionToGenerate.prompt && sectionToGenerate.prompt.trim()
        ? sectionToGenerate.prompt
        : await getDefaultPromptForSection(sectionToGenerate.title, actualProjectId);

      // Build context for Section 2, 3, 4 using protocol sections
      let contextToUse = sectionToGenerate.context || '';
      if (sectionToGenerate.title === '2. INTRODUCTION AND STUDY OVERVIEW') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 2: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4.1 Description of Trial Design',
              ],
            },
          ];
          let protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            const idx = protocolContext.toLowerCase().indexOf('decentralized trial elements'.toLowerCase());
            if (idx !== -1) {
              protocolContext = protocolContext.substring(0, idx);
            }
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 2 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 2 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '3. STUDY OBJECTIVES, ESTIMANDS, AND ENDPOINTS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 3: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 3 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 3 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '4. ENDPOINTS AND BASELINE VARIABLES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 4: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 4 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 4 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '5. HYPOTHESES AND SAMPLE SIZE CONSIDERATIONS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 5: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4. TRIAL DESIGN',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 5 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 5 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '6. ANALYSIS SETS (POPULATIONS FOR ANALYSIS)') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 6: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '10.2 Analysis Sets',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 6 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 6 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '7. GENERAL STATISTICAL METHODOLOGY AND CONVENTIONS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 7: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 7 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 7 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '8. BASELINE, STUDY CONDUCT, AND PARTICIPANT CHARACTERISTICS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 8: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '1. PROTOCOL SUMMARY',
                '2. INTRODUCTION',
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '4. TRIAL DESIGN',
                '5. TRIAL POPULATION',
                '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
                '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
                '10. STATISTICAL CONSIDERATIONS',
                '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
                '12. APPENDIX: SUPPORTING DETAILS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== FULL PROTOCOL CONTEXT FOR SECTION 8 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 8 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '9. EFFICACY ANALYSES AND SUMMARIES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 9: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4. TRIAL DESIGN',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 9 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 9 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '10. SAFETY ANALYSES AND SUMMARIES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 10: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '12. APPENDIX: SUPPORTING DETAILS',
                '1.3 SCHEDULE OF ACTIVITIES',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 10 (SAFETY) ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 10 generation:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToGenerate.title === '11. PHARMACOKINETIC (PK) ANALYSES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 11: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToGenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '1.3 SCHEDULE OF ACTIVITIES',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 11 (PK) ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 11 generation:', e);
        }
        contextToUse = parts.join('');
      }

      const generatedOutput = await generateContent(
        promptToUse,
        contextToUse,
        '',
        'sap',
        sectionToGenerate.title
      );
      // Save the generated output (backend will create version)
      await handleInputChange(_id, 'output', generatedOutput, 'aidrafted');
      // Clear edited text content for this section
      setEditedTextContent(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
      fetchSections();
    } catch (err) {
      console.error('Error generating SAP content:', err);
      setError(`Failed to generate content: ${err.message}`);
    } finally {
      setIsGenerating((prev) => ({ ...prev, [_id]: false }));
    }
  };

  // Regenerate content handler (regenerates existing content)
  const handleRegenerateContent = async (_id) => {
    setIsGenerating((prev) => ({ ...prev, [_id]: true }));
    setError(null);
    try {
      const sectionToRegenerate = sections.find((s) => s._id === _id);
      if (!sectionToRegenerate) {
        throw new Error('SAP section not found for content regeneration.');
      }
      // Use user prompt if present, else default prompt
      let promptToUse = sectionToRegenerate.prompt && sectionToRegenerate.prompt.trim()
        ? sectionToRegenerate.prompt
        : await getDefaultPromptForSection(sectionToRegenerate.title, actualProjectId);

      // Build context for Section 2, 3, 4 using protocol sections
      let contextToUse = sectionToRegenerate.context || '';
      if (sectionToRegenerate.title === '2. INTRODUCTION AND STUDY OVERVIEW') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 2: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4.1 Description of Trial Design',
              ],
            },
          ];
          let protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            const idx = protocolContext.toLowerCase().indexOf('decentralized trial elements'.toLowerCase());
            if (idx !== -1) {
              protocolContext = protocolContext.substring(0, idx);
            }
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 2 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 2 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '3. STUDY OBJECTIVES, ESTIMANDS, AND ENDPOINTS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 3: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 3 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 3 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '4. ENDPOINTS AND BASELINE VARIABLES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 4: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 4 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 4 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '5. HYPOTHESES AND SAMPLE SIZE CONSIDERATIONS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 5: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4. TRIAL DESIGN',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 5 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 5 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '6. ANALYSIS SETS (POPULATIONS FOR ANALYSIS)') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 6: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '10.2 Analysis Sets',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 6 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 6 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '7. GENERAL STATISTICAL METHODOLOGY AND CONVENTIONS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 7: Use ONLY the following. Do not use other protocol sections.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTION FOR SECTION 7 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 7 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '8. BASELINE, STUDY CONDUCT, AND PARTICIPANT CHARACTERISTICS') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 8: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '1. PROTOCOL SUMMARY',
                '2. INTRODUCTION',
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '4. TRIAL DESIGN',
                '5. TRIAL POPULATION',
                '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
                '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
                '10. STATISTICAL CONSIDERATIONS',
                '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
                '12. APPENDIX: SUPPORTING DETAILS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== FULL PROTOCOL CONTEXT FOR SECTION 8 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 8 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '9. EFFICACY ANALYSES AND SUMMARIES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 9: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '4. TRIAL DESIGN',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '10. STATISTICAL CONSIDERATIONS',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 9 ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 9 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '10. SAFETY ANALYSES AND SUMMARIES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 10: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '12. APPENDIX: SUPPORTING DETAILS',
                '1.3 SCHEDULE OF ACTIVITIES',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 10 (SAFETY) ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 10 regeneration:', e);
        }
        contextToUse = parts.join('');
      } else if (sectionToRegenerate.title === '11. PHARMACOKINETIC (PK) ANALYSES') {
        const parts = [];
        parts.push('CONTEXT FOR SECTION 11: Use ONLY the following. Do not use other protocol sections outside this context.');
        parts.push('\n--- SAP INTAKE FORM DATA ---\n');
        parts.push(sectionToRegenerate.context || '(none provided)');
        try {
          const { fetchProtocolContextForICF } = await import('../../utils/protocolContextFetcher');
          const protocolSources = [
            {
              type: 'PROTOCOL',
              titles: [
                '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
                '8. TRIAL ASSESSMENTS AND PROCEDURES',
                '1.3 SCHEDULE OF ACTIVITIES',
              ],
            },
          ];
          const protocolContext = await fetchProtocolContextForICF(actualProjectId, protocolSources);
          if (protocolContext && protocolContext.trim()) {
            parts.push('\n=== RELEVANT PROTOCOL SECTIONS FOR SECTION 11 (PK) ===\n');
            parts.push(protocolContext.trim());
          }
        } catch (e) {
          console.warn('Could not fetch protocol context for Section 11 regeneration:', e);
        }
        contextToUse = parts.join('');
      }

      const regeneratedOutput = await generateContent(
        promptToUse,
        contextToUse,
        '',
        'sap',
        sectionToRegenerate.title
      );
      // Save the regenerated output (backend will create version)
      await handleInputChange(_id, 'output', regeneratedOutput, 'aidrafted');
      // Clear edited text content for this section
      setEditedTextContent(prev => {
        const updated = { ...prev };
        delete updated[_id];
        return updated;
      });
      fetchSections();
    } catch (err) {
      console.error('Error regenerating SAP content:', err);
      setError(`Failed to regenerate content: ${err.message}`);
    } finally {
      setIsGenerating((prev) => ({ ...prev, [_id]: false }));
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all SAP sections for this project? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        await documentEditorService.deleteAllSAPSections(actualProjectId); // Pass projectId
        setSections([]);
        setError(null);
        // Re-fetch predefined sections to re-populate after reset
        await fetchSections();
      } catch (error) {
        console.error('Error resetting SAP sections:', error);
        setError(`Failed to reset SAP sections: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExportSapPdfNative = async () => {
    startSpan('ui_sap_export_pdf_native', { page: 'sap_editor', meta: { projectId: actualProjectId || null } });
    try {
      const printableSections = (sections || []).filter(
        (s) => s && s.title && s.output && String(s.output).trim()
      );
      if (printableSections.length === 0) {
        alert('No SAP content available to export as PDF.');
        endSpan('ui_sap_export_pdf_native', { status: 'error' });
        return;
      }

      let blob = await pdf(
        <SAPPdfDocument
          sections={printableSections}
          trialSchemaImageUrl={trialSchemaImageUrl}
        />
      ).toBlob();

      // Insert a real, hyperlinked, page-numbered Table of Contents — same approach as
      // the protocol PDF export (see nativeProtocolPdfToc.js), since @react-pdf/renderer
      // has no auto-updating TOC field the way the Word export's TableOfContents does.
      try {
        const tocEntries = buildSapPdfTocEntries(printableSections);
        if (tocEntries.length > 0) {
          const { insertProtocolPdfToc } = await import('../../utils/nativeProtocolPdfToc');
          blob = await insertProtocolPdfToc(blob, tocEntries, { titlePageEnabled: true });
        }
      } catch (tocErr) {
        console.warn('SAP native PDF TOC insertion failed; exporting without a TOC:', tocErr?.message || tocErr);
      }

      try {
        const { stampProtocolPdfPageNumbers } = await import('../../utils/stampProtocolPdfPageNumbers');
        blob = await stampProtocolPdfPageNumbers(blob, { skipFirstPage: true });
      } catch (pageNumErr) {
        console.warn('SAP native PDF page numbering failed:', pageNumErr?.message || pageNumErr);
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'statistical-analysis-plan.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      endSpan('ui_sap_export_pdf_native', { status: 'ok' });
    } catch (e) {
      console.error('Failed to export SAP as PDF:', e);
      alert('Failed to export SAP as PDF.');
      endSpan('ui_sap_export_pdf_native', { status: 'error' });
    }
  };

  const handleDeleteSection = async (_id) => {
    if (window.confirm('Are you sure you want to delete this SAP section? This action cannot be undone.')) {
      try {
        setIsLoading(true);
        await documentEditorService.deleteSAPSection(_id, actualProjectId); // Pass projectId
        setSections((prev) => prev.filter((section) => section._id !== _id));
        setError(null);
      } catch (error) {
        console.error('Error deleting SAP section:', error);
        setError(`Failed to delete SAP section: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleMarkAsFinal = async (_id) => {
    try {
      // Update backend
      await documentEditorService.updateSAPSection(_id, { final: true }, actualProjectId);
      // Update UI
      setSections(prevSections =>
        prevSections.map(section =>
          section._id === _id ? { ...section, final: true } : section
        )
      );
    } catch (err) {
      console.error("Failed to mark SAP section as final:", err);
      alert('Failed to mark SAP section as final.');
    }
  };

  const handleExport = async (formatType) => {
    if (formatType === 'word') {
      exportToWord(sections);
    } else if (formatType === 'xml') {
      exportToXML(sections);
    }
  };

  const exportToWord = async (sectionsToExport) => {
    try {
      setIsExporting(true);
      const children = [];

      // Dynamic TOC: Word-native TableOfContents using headings from SAP sections
      children.push(...generateSapTableOfContents());

      // Page break before SAP sections start
      children.push(
        new Paragraph({
          children: [],
          pageBreakBefore: true,
        })
      );

      (sectionsToExport || []).forEach((section, idx) => {
        if (!section || !section.title) return;

        if (idx > 0) {
          children.push(
            new Paragraph({
              children: [],
              pageBreakBefore: true,
            })
          );
        }

        children.push(
          new Paragraph({
            text: section.title,
            heading: HeadingLevel.HEADING_1,
          })
        );

        const rawOutput = section.output && String(section.output).trim();
        if (rawOutput) {
          const blocks = richTextToBlocks(rawOutput);
          // The heading above already renders the section title once — drop
          // a leading heading in the generated content if it just restates
          // that same title (e.g. "3.0 STATISTICAL FRAMEWORK" under
          // "3. Statistical Framework"), so it isn't printed twice.
          if (blocks[0]?.type === 'heading' && isDuplicateTitleHeading(blocks[0].text, section.title)) {
            blocks.shift();
          }
          children.push(...blocksToDocxChildren(blocks));
        } else {
          children.push(new Paragraph({ text: 'No content generated' }));
        }

        children.push(new Paragraph({ text: '' }));
      });

      const doc = new Document({
        sections: [
          {
            properties: {},
            children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sap-document.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to Word:', error);
      setError('Failed to export document to Word format.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToXML = (sectionsToExport) => {
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<document>\n';
    // Sort sections by title (e.g., "1. Introduction", "1.1 Purpose")
    const sortedSections = [...sectionsToExport].sort((a, b) => {
      const aTitle = a.title || '';
      const bTitle = b.title || '';
      return aTitle.localeCompare(bTitle, undefined, { numeric: true });
    });

    for (const section of sortedSections) {
      if (!section.title || !section.output) continue; // Skip if no title or output
      const match = section.title.match(/^(\d+)(\.\d+)*/); // Extract main number (e.g., "1", "1.1")
      const mainNum = match ? match[0] : '';
      const sectionTitle = section.title.replace(/^(\d+(\.\d+)*\.?\s*)/, '').trim(); // Remove number prefix
      const tagName = toCamelCase(sectionTitle) || 'section';

      const parsed = parseSectionContent(section.output, mainNum);
      const sectionObj = {
        number: mainNum,
        title: sectionTitle,
        main: parsed.main,
        subsections: parsed.subsections
      };
      xmlContent += jsonToXml(sectionObj, tagName, '  ') + '\n';
    }
    xmlContent += '</document>';

    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'sap_protocol.xml');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleViewVersionHistory = (_id) => {
    navigate(`/sap-section-history/${_id}?projectId=${actualProjectId}`);
  };

  const handleAddSection = async () => {
    const title = tabValue === 0 ? selectedSectionTitle : customSectionTitle;
    if (!title) {
      alert('Please select or enter a section title.');
      return;
    }

    try {
      const sectionData = {
        title,
        output: '',
        projectId: actualProjectId,
        documentType: 'SAP' // Add documentType
      };
      const response = await documentEditorService.createSAPSection(sectionData, actualProjectId);
      setSections([...sections, response.data]);
      handleCloseAddDialog();
      // Re-fetch sections to ensure order and latest data
      fetchSections();
    } catch (error) {
      console.error('Error adding section:', error);
      alert('Failed to add section. It might already exist or there was a server error.');
    }
  };

  const handleRevertVersion = async (sectionId, versionData) => {
    try {
      const updatedSection = await documentEditorService.updateSAPSection(
        sectionId,
        { output: versionData.content },
        actualProjectId // Pass projectId
      );
      setSections(sections.map(s => s._id === sectionId ? { ...s, output: updatedSection.data.output } : s));
      alert('SAP section reverted to selected version!');
    } catch (error) {
      console.error('Error reverting SAP section version:', error);
      alert('Failed to revert SAP section version.');
    }
  };

  const handleMenuOpen = (event, _id) => {
    setAnchorEl(event.currentTarget);
    setSelectedSectionId(_id);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Pre-select the first available title or clear custom title
    setSelectedSectionTitle(availableSectionTitles[0] || '');
    setCustomSectionTitle('');
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedSectionTitle('');
    setCustomSectionTitle('');
    setTabValue(0); // Reset to template tab
  };

  const handleSectionTitleChange = (event) => {
    setSelectedSectionTitle(event.target.value);
    setCustomSectionTitle(''); // Clear custom title if a predefined one is selected
  };

  const handleCustomTitleChange = (event) => {
    setCustomSectionTitle(event.target.value);
    setSelectedSectionTitle(''); // Clear predefined title if a custom one is entered
  };

  if (isLoading || projectContextLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || projectContextError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || projectContextError}</Alert>
      </Box>
    );
  }

  if (!actualProjectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please select a project to view its documents.</Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        border: '2px solid #1976d2',
        borderRadius: 4,
        p: 4,
        mt:4,
        background: 'linear-gradient(135deg, #f8fafc 60%, #e3f2fd 100%)',
        boxShadow: '0 8px 32px 0 rgba(25, 118, 210, 0.18), 0 1.5px 0 #fff inset',
        mb: 4,
        position: 'relative',
        overflow: 'visible',
      }}>
        <Typography variant="h4" gutterBottom>SAP Editor</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Your SAP is taking shape....
        </Alert>
        <AppBar
          position="static"
          color="transparent"
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(30, 115, 190, 0.05) 0%, rgba(22, 138, 173, 0.05) 100%)',
          }}
        >
          <Toolbar variant="dense" sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, py: 1 }}>
            <Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                sx={{ mr: 1 }}
              >
                Add Section
              </Button>
              <Button
                variant="outlined"
                startIcon={<ResetIcon />}
                onClick={handleReset}
                sx={{ mr: 1 }}
              >
                Reset All Sections
              </Button>
              <Tooltip title={`${mode === 'edit' ? 'Switch to Preview Mode' : 'Switch to Edit Mode'} (Ctrl+P)`}>
                <Button
                  variant="outlined"
                  startIcon={mode === 'edit' ? <VisibilityIcon /> : <EditIcon />}
                  onClick={() => setMode(mode === 'edit' ? 'preview' : 'edit')}
                  sx={{
                    mr: 1,
                    background: mode === 'edit' 
                      ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                      : 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                    color: 'white',
                    '&:hover': {
                      background: mode === 'edit' ? '#1565c0' : '#388e3c'
                    }
                  }}
                >
                  {mode === 'edit' ? 'Preview Mode' : 'Edit Mode'}
                </Button>
              </Tooltip>
            </Box>
            <Box>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => handleExport('word')}
                sx={{ mr: 1 }}
              >
                Export to DOCX
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={() => handleExport('xml')}
                sx={{ mr: 1 }}
              >
                Export to XML
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExportSapPdfNative}
              >
                Export to PDF (Native)
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        <Grid container spacing={3}>
            {sections.map((section) => (
              <Grid item xs={12} key={section._id}>
                <Box sx={{
                  border: '1.5px solid #90caf9',
                  borderRadius: 3,
                  background: 'linear-gradient(120deg, #fff 80%, #e3f2fd 100%)',
                  p: 3,
                  mb: 4,
                  boxShadow: '0 4px 24px 0 rgba(25, 118, 210, 0.12), 0 1.5px 0 #fff inset',
                  position: 'relative',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 12px 32px 0 rgba(25, 118, 210, 0.22), 0 2px 0 #fff inset',
                    transform: 'translateY(-2px) scale(1.01)',
                  },
                }}>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', pb: 1 }}>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 2, fontWeight: 700 }}>
                      Section: {section.title}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Copy Section">
                      <IconButton size="small"><CopyIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Version History">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewVersionHistory(section._id)}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More Options">
                      <IconButton size="small" onClick={(event) => handleMenuOpen(event, section._id)}>
                        <MoreIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Tabs
                    value={sectionTabs[section._id] || 0}
                    onChange={(_, v) => {
                      setSectionTabs(tabs => ({ ...tabs, [section._id]: v }));
                      if (v === 1) fetchSectionVersions(section._id);
                    }}
                    sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px 0 rgba(25, 118, 210, 0.10)', background: 'linear-gradient(90deg, #e3f2fd 60%, #fff 100%)', p: 1 }}
                  >
                    <Tab label="Edit" />
                    <Tab label="Version History" />
                  </Tabs>
                  { (sectionTabs[section._id] || 0) === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                        {mode === 'edit' ? 'Generated Content' : 'Document Preview'}
                      </Typography>
                      {mode === 'edit' ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={18}
                          value={editedTextContent[section._id] || (() => {
                            try {
                              // Initialize with extracted text content if not already set
                              const tempDiv = document.createElement('div');
                              tempDiv.innerHTML = section.output || '';
                              return tempDiv.textContent || tempDiv.innerText || '';
                            } catch (error) {
                              console.error('Error extracting text content:', error);
                              return section.output || '';
                            }
                          })()}
                          onChange={(e) => {
                            handleTextContentChange(section._id, 'output', e.target.value);
                          }}
                          variant="outlined"
                          size="small"
                          placeholder="Edit text content here (HTML tags are automatically preserved)..."
                          helperText="Only the text content can be edited. HTML formatting is protected."
                        />
                      ) : (
                        <>
                          <Box sx={{ 
                            minHeight: 200, 
                            bgcolor: '#ffffff', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 2, 
                            p: 3, 
                            fontFamily: 'Times New Roman, serif',
                            fontSize: 14,
                            color: '#333333',
                            lineHeight: 1.6,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              mt: 2,
                              mb: 1
                            },
                            '& h1': { fontSize: '1.5em' },
                            '& h2': { fontSize: '1.3em' },
                            '& h3': { fontSize: '1.1em' },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 2, mb: 1 },
                            '& li': { mb: 0.5 },
                            '& table': { 
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f8f9fa',
                              fontWeight: 'bold'
                            }
                          }}>
                            {section.output && section.output.trim() ? (
                              <Box dangerouslySetInnerHTML={{ __html: section.output }} />
                            ) : (
                              <Box sx={{ 
                                color: '#999',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                py: 4
                              }}>
                                [No content available for this section]
                              </Box>
                            )}
                          </Box>
                          {section.title === '2. INTRODUCTION AND STUDY OVERVIEW' && trialSchemaImageUrl && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 2, 
                              bgcolor: 'grey.50', 
                              borderRadius: 2, 
                              border: '1px dashed #90caf9' 
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                2.4 Schema
                              </Typography>
                              <Box sx={{ textAlign: 'center' }}>
                                {trialSchemaImageUrl.trim().startsWith('<svg') ? (
                                  <img
                                    src={`data:image/svg+xml;utf8,${encodeURIComponent(trialSchemaImageUrl)}`}
                                    alt="Trial Schema"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: 260,
                                      border: '1px solid #ccc',
                                      borderRadius: 8,
                                      background: '#fff'
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={trialSchemaImageUrl}
                                    alt="Trial Schema"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: 260,
                                      border: '1px solid #ccc',
                                      borderRadius: 8,
                                      background: '#fff'
                                    }}
                                  />
                                )}
                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                  (Auto-fetched from current protocol trial schema)
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Prompt</Typography>
                      {mode === 'edit' ? (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: 'column' }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={section.prompt || ''}
                            onChange={(e) => handleInputChange(section._id, 'prompt', e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder="Optional: Enter a custom prompt for this section. Leave empty to use the default prompt when generating content."
                          />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="contained" 
                              size="small"
                              disabled={isGenerating[section._id]}
                              onClick={() => handleGenerateContent(section._id)}
                              sx={{ flex: 1, minWidth: 150 }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Generate Content'}
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<RefreshIcon />}
                              size="small"
                              disabled={isGenerating[section._id] || !section.output}
                              onClick={() => handleRegenerateContent(section._id)}
                              sx={{ flex: 1, minWidth: 150 }}
                            >
                              {isGenerating[section._id] ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Regenerate'}
                            </Button>
                            <Button
                              variant={section.final ? "outlined" : "contained"}
                              color={section.final ? "success" : "primary"}
                              size="small"
                              disabled={section.final}
                              onClick={() => handleMarkAsFinal(section._id)}
                              sx={{ flex: 1, minWidth: 150 }}
                            >
                              {section.final ? 'Section is Final' : 'Mark as Final'}
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<SaveIcon />}
                              size="small"
                              onClick={async () => {
                                const sectionObj = sections.find(s => s._id === section._id);
                                if (!sectionObj) return;
                                await documentEditorService.updateSAPSection(section._id, { 
                                  output: sectionObj.output, 
                                  prompt: sectionObj.prompt, 
                                  context: sectionObj.context 
                                }, actualProjectId);
                                fetchSections();
                                fetchSectionVersions(section._id);
                              }}
                              sx={{ flex: 1, minWidth: 150 }}
                            >
                              Save & Version
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{
                          minHeight: 100,
                          bgcolor: 'grey.50',
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          p: 2,
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                          fontSize: 15,
                          color: 'text.secondary',
                        }}>
                          {section.prompt || <span style={{ color: '#aaa' }}>No custom prompt - will use default prompt when generating content</span>}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  )}
                  { (sectionTabs[section._id] || 0) === 1 && (
                    <Box>
                      {sectionVersions[section._id]?.length > 0 ? (
                        sectionVersions[section._id].map((version, idx) => (
                          <Paper key={version._id || idx} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Version {version.versionNumber}</Typography>
                            <Typography variant="caption" color="text.secondary">{version.createdAt ? new Date(version.createdAt).toLocaleString() : ''}</Typography>
                            {version.prompt && (
                              <Typography variant="body2" sx={{ mt: 1 }}><strong>Prompt:</strong> {version.prompt}</Typography>
                            )}
                            {version.output && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Content:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    p: 1,
                                    fontSize: 14,
                                    lineHeight: 1.5,
                                    bgcolor: '#fafafa',
                                  }}
                                  dangerouslySetInnerHTML={{ __html: version.output }}
                                />
                              </Box>
                            )}
                            <Button size="small" sx={{ mt: 1 }} onClick={async () => {
                              await handleRevertVersion(section._id, version);
                              fetchSectionVersions(section._id);
                            }}>Revert to this Version</Button>
                </Paper>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No version history available for this section.</Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleViewVersionHistory(selectedSectionId)}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Version History</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDeleteSection(selectedSectionId)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Section</ListItemText>
          </MenuItem>
        </Menu>

        {/* Scroll to Top Button */}
        <ScrollToTop />
      </Box>
    </ThemeProvider>
  );
};

export default SAPEditor; 